/* eslint-disable no-console */
class PubSub {
  events = {
    addToCart: { name: "add-to-cart" },
    updateDOM: { name: "update-dom" },
    cartUpdated: { name: "cart-updated" },
    cartReady: { name: "cart-ready", replay: true },
    productViewed: { name: "product-viewed" },
    contentInteracted: { name: "content-interacted" },
    containerShown: { name: "container-shown" },
    preferredStoreChange: { name: "preferred-store-change", replay: true },
    storesFetched: { name: "stores-fetched", replay: true },
    storesFiltered: { name: "stores-filtered" },
    storesSorted: { name: "stores-sorted", replay: true },
    googleMapReady: { name: "google-map-ready", replay: true },
    userLocationChange: { name: "user-location-change", replay: true },
  };

  #listeners = new Map();
  #last = new Map();

  /**
   * Subscribes to an event. Events with `replay: true` immediately invoke the
   * callback with the last published payload, if one exists.
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {Function} callback - Called with the published data
   * @returns {Function} Unsubscribe function
   */
  subscribe(event, callback) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return () => {};
    }

    if (typeof callback !== "function") {
      console.warn("PubSub: Callback must be a function");
      return () => {};
    }

    if (!this.#listeners.has(eventName)) {
      this.#listeners.set(eventName, new Set());
    }

    const listeners = this.#listeners.get(eventName);

    if (listeners.has(callback)) {
      return () => this.unsubscribe(eventName, callback);
    }

    listeners.add(callback);

    if (this.#shouldReplay(eventName) && this.#last.has(eventName)) {
      this.#notify(callback, this.#last.get(eventName));
    }

    return () => this.unsubscribe(eventName, callback);
  }

  /**
   * Unsubscribes from an event
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {Function} callback - The function to unsubscribe
   */
  unsubscribe(event, callback) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    this.#listeners.get(eventName)?.delete(callback);
  }

  /**
   * Publishes data to subscribers
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   * @param {*} data - The data to publish
   */
  publish(event, data = {}) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    if (this.#shouldReplay(eventName)) {
      this.#last.set(eventName, data);
    }

    // Snapshot so subscribers that subscribe/unsubscribe during notify
    // (e.g. live-region replaceWith) do not extend or skip this publish.
    [...(this.#listeners.get(eventName) ?? [])].forEach((callback) => {
      this.#notify(callback, data);
    });
  }

  /**
   * Clears the retained payload for a replay event
   * @param {string|{ name: string, replay?: boolean }} event - Event name or definition
   */
  clear(event) {
    const eventName = this.#eventName(event);

    if (this.unknownEvent(eventName)) {
      console.warn(`PubSub: Unknown event "${eventName}"`);
      return;
    }

    this.#last.delete(eventName);
  }

  /**
   * @param {string} eventName
   * @returns {boolean}
   */
  unknownEvent(eventName) {
    if (typeof eventName !== "string" || eventName.length === 0) {
      return true;
    }

    return !Object.values(this.events).some(({ name }) => name === eventName);
  }

  /**
   * @param {string|{ name: string, replay?: boolean }} event
   * @returns {string|undefined}
   */
  #eventName(event) {
    if (typeof event === "string") {
      return event;
    }

    if (event && typeof event === "object" && typeof event.name === "string") {
      return event.name;
    }

    return undefined;
  }

  /**
   * @param {string} eventName
   * @returns {boolean}
   */
  #shouldReplay(eventName) {
    return Object.values(this.events).some(
      ({ name, replay }) => name === eventName && replay,
    );
  }

  /**
   * @param {Function} callback
   * @param {*} data
   */
  #notify(callback, data) {
    try {
      callback(data);
    } catch (error) {
      console.error("PubSub: Subscriber error", error);
    }
  }
}

WYATT.PubSub = new PubSub();

export default PubSub;
